import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const loadReCaptchaV3 = (siteKey) =>
    new Promise((resolve, reject) => {
      if (window.grecaptcha && window.grecaptcha.execute) {
        resolve(window.grecaptcha);
        return;
      }

      const existing = document.getElementById('recaptcha-v3');
      if (!existing) {
        const script = document.createElement('script');
        script.id = 'recaptcha-v3';
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error('Impossibile caricare reCAPTCHA.'));
        document.body.appendChild(script);
      }

      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.execute) {
          window.clearInterval(timer);
          resolve(window.grecaptcha);
          return;
        }
        if (Date.now() - startedAt > 6000) {
          window.clearInterval(timer);
          reject(new Error('Timeout caricamento reCAPTCHA.'));
        }
      }, 100);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      let token;
      if (recaptchaSiteKey) {
        const grecaptcha = await loadReCaptchaV3(recaptchaSiteKey);
        token = await grecaptcha.execute(recaptchaSiteKey, { action: 'login' });
      }

      const result = await login(email, password, token);
      
      if (result.success) {
        if (result.first_login) {
          // Reindirizza alla pagina di cambio password se è il primo accesso
          navigate('/change-password');
        } else {
          // Reindirizza alla dashboard
          navigate('/dashboard');
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Si è verificato un errore durante il login');
      console.error(err);
    }
    
    setLoading(false);
  };

  const openForgot = () => {
    setForgotMessage('');
    setForgotEmail('');
    setForgotOpen(true);
  };

  const closeForgot = () => setForgotOpen(false);

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    setForgotMessage('');
    try {
      await authService.forgotPassword(forgotEmail);
      setForgotMessage('Se l\'email esiste, riceverai una password temporanea.');
    } catch (err) {
      setForgotMessage('Se l\'email esiste, riceverai una password temporanea.');
    }
    setForgotLoading(false);
  };

  return (
    <div className="mx-auto mt-8 w-full max-w-md">
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Accedi</h1>
              <p className="mt-1 text-sm font-medium text-slate-500">Banca del Tempo</p>
            </div>
            <img src="/Logo.png" alt="Logo" className="h-11 w-11 rounded-xl" />
          </div>

          {error ? <div className="alert-error mt-4">{error}</div> : null}

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div>
              <label htmlFor="email" className="label">
                Indirizzo Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input mt-1"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input mt-1"
              />
            </div>

            <button type="submit" className="btn-primary w-full" disabled={loading}>
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </button>

            <div className="flex flex-col gap-2">
              <button type="button" onClick={openForgot} className="btn-ghost w-full">
                Password dimenticata?
              </button>
              <button type="button" onClick={() => navigate('/register')} className="btn-ghost w-full">
                Non hai un account? Registrati
              </button>
            </div>
          </form>
        </div>
      </div>

      <div
        className={[
          'fixed inset-0 z-[70] flex items-center justify-center px-4 transition',
          forgotOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
        aria-hidden={!forgotOpen}
      >
        <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={closeForgot} />
        <div className="relative w-full max-w-md">
          <div className="card">
            <div className="card-body">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-bold text-slate-900">Recupero Password</div>
                  <p className="mt-1 text-sm text-slate-600">
                    Inserisci il tuo indirizzo email. Se l&apos;account esiste e risulta attivo, riceverai una password temporanea via email.
                  </p>
                </div>
                <button type="button" onClick={closeForgot} className="btn-ghost">
                  Chiudi
                </button>
              </div>

              <form onSubmit={handleForgotSubmit} className="mt-4 space-y-3">
                <div>
                  <label htmlFor="forgotEmail" className="label">
                    Email
                  </label>
                  <input
                    id="forgotEmail"
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="input mt-1"
                  />
                </div>

                {forgotMessage ? <div className="alert-info">{forgotMessage}</div> : null}

                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <button type="button" onClick={closeForgot} className="btn-outline sm:w-auto">
                    Annulla
                  </button>
                  <button type="submit" className="btn-primary sm:w-auto" disabled={forgotLoading}>
                    {forgotLoading ? 'Invio...' : 'Invia'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
